<template>
  <div
    data-test="war_room_comments"
    :class="{ '--member-post': isTopLevelThread && isMemberPost && !$auth.loggedIn }"
    class="war-room-comments"
  >
    <WarRoomCommentCreateForm
      v-if="$auth.loggedIn && isTopLevelThread"
      ref="commentCreateForm"
      :autofocus="!!$route.query.comment"
      :boost-usernames="boostUsernames"
      class="war-room-comments__top-level-form"
      :upserting="upserting"
      data-test="war_room_top_comment_form"
      :initial-text="commentsFormInitialText"
      @post-comment="onPostComment"
    />
    <WarRoomComment
      v-for="(comment, index) in visibleComments"
      :key="comment.id"
      :boost-usernames="boostUsernames"
      :comment="comment"
      :index="index"
      :nesting-level="nestingLevel"
      :parent-id="parentId"
      :post="post"
    />
    <button v-if="isHiddenCommentsToggleVisible" class="war-room-comments__show-hidden" @click="toggleHiddenComments">
      {{ `Show ${hiddenCommentsCount} more replies` }}
    </button>
  </div>
</template>

<script>
import { mapMutations } from 'vuex';
import WarRoomCommentCreateForm from '@/components/partials/war-room/WarRoomCommentCreateForm/WarRoomCommentCreateForm';
import WarRoomComment from '@/components/partials/war-room/WarRoomComment/WarRoomComment';
import { LAST_NESTING_LEVEL } from '~/components/partials/war-room/constants';
import { createBoostUsernames } from '~/components/partials/war-room/WarRoomComments/utils';
import { calculateUpvote } from '~/components/partials/war-room/utils';

const LAST_NESTING_LEVEL_VISIBLE_COMMENT_COUNT = 1;

export default {
  name: 'WarRoomComments',
  components: {
    WarRoomCommentCreateForm,
    WarRoomComment,
  },
  props: {
    post: {
      type: Object,
      default: () => ({}),
    },
    nestingLevel: {
      type: Number,
      default: 1,
    },
    parentId: {
      type: Number,
      default: null,
    },
    boostUsernamesNested: {
      type: Array,
      default: () => [],
    },
    comments: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isHiddenCommentsVisible: false,
      upserting: false,
    };
  },
  computed: {
    isMemberPost() {
      return Boolean(this.post.members_only);
    },
    isTopLevelThread() {
      return this.nestingLevel === 1;
    },
    isLastNestingLevel() {
      return this.nestingLevel === LAST_NESTING_LEVEL;
    },
    hasHighlightedComment() {
      return !!this.$route.params.commentId;
    },
    isHighlightedCommentInThread() {
      if (!this.hasHighlightedComment) return false;

      return this.comments.findIndex(({ id }) => id === Number(this.$route.params.commentId)) !== -1;
    },
    visibleComments() {
      if (
        this.isLastNestingLevel &&
        this.comments.length > LAST_NESTING_LEVEL_VISIBLE_COMMENT_COUNT &&
        !this.isHiddenCommentsVisible &&
        !this.threadHasNewComments &&
        !this.isHighlightedCommentInThread
      ) {
        return this.comments.slice(0, LAST_NESTING_LEVEL_VISIBLE_COMMENT_COUNT);
      }

      return this.comments;
    },
    threadHasNewComments() {
      return this.comments.some(({ isNew }) => isNew);
    },
    boostUsernames() {
      if (this.isTopLevelThread) {
        return createBoostUsernames(this.post, this.comments);
      }

      return this.boostUsernamesNested;
    },
    hiddenCommentsCount() {
      if (!this.isLastNestingLevel) {
        return 0;
      }

      return this.comments.length - LAST_NESTING_LEVEL_VISIBLE_COMMENT_COUNT;
    },
    isHiddenCommentsToggleVisible() {
      return (
        this.isLastNestingLevel &&
        this.hiddenCommentsCount > 0 &&
        !this.isHiddenCommentsVisible &&
        !this.threadHasNewComments &&
        !this.isHighlightedCommentInThread
      );
    },
    isPostAuthor() {
      return this.$auth.loggedIn && this.$auth.user.username === this.post.user_username;
    },
    commentsFormInitialText() {
      return this.$route.query.mention ? `Congrats! @${this.post.user_username}` : '';
    },
  },
  methods: {
    ...mapMutations('warRoom', ['addComment', 'addUsers', 'updatePost']),
    // create new comment
    async onPostComment({ text, media, is_anonymous }) {
      this.upserting = true;
      try {
        const {
          data: { comment_dict: commentDict, user_dict: userDict, public_user_dict: publicUserDict },
        } = await this.$api.post('/api/war_room/comments.json', {
          comment: {
            text,
            is_anonymous,
            post_id: this.post.id,
            parent_id: null,
            media,
          },
        });

        const createdComment = Object.values(commentDict)[0];

        this.$analytics.logEvent('war-room__1-post__comment__create', {
          comment_id: createdComment?.id,
          replyTo: 'post',
        });

        this.addUsers({ userDict, publicUserDict });
        Object.values(commentDict).forEach(comment => this.addComment({ post: this.post, comment }));
        this.upvotePost();
      } catch (e) {
        this.$notice.error(e);
        this.$sentryUtils.error(e);
      } finally {
        this.upserting = false;
      }
    },
    toggleHiddenComments() {
      this.isHiddenCommentsVisible = !this.isHiddenCommentsVisible;

      this.$analytics.logEvent('war-room__1-post__hidden-comments-link__click');
    },
    upvotePost() {
      if (this.isPostAuthor) return;
      if (this.nestingLevel > 1) return;
      if (![null, 0].includes(this.post.current_vote)) return;

      const { nextRating } = calculateUpvote(this.post, 1);
      const attributes = { rating: nextRating, current_vote: 1 };

      this.updatePost({ post: this.post, attributes });
    },
  },
};
</script>

<style scoped lang="scss">
.war-room-comments {
  &__show-hidden {
    font: inherit;
    background: transparent;
    border: none;
    margin-top: 8px;
    padding: 4px 0 4px 12px;
    color: var(--blue-60);
    cursor: pointer;

    @include body-semibold;

    @include mq('sm') {
      padding: 4px 0;
    }

    &:focus,
    &:hover {
      text-decoration: underline;
    }
  }

  // That prop works with GateWidget overlay
  &.--member-post {
    position: relative;
    z-index: 0;
    max-height: 300px;
    pointer-events: none;
    overflow: hidden;
    user-select: none;
  }
}
</style>
